import React from 'react';
import PropTypes from 'prop-types';
import Control from '@ott/control';

import cls from 'classnames/bind';
import _omit from 'lodash/omit';
import _noop from 'lodash/noop';

const isServerSideRender = typeof window === 'undefined';

const isPlaceholderSupported =
  !isServerSideRender && 'placeholder' in document.createElement('input');

import s from './RainbowInput.scss';
import { deprecatedPropType } from '@ott/react-utils';

const cx = cls.bind(s);

class RainbowInput extends Control {
  static displayName = 'RainbowInput';

  static propTypes = {
    ...Control.propTypes,
    additionalInfo: PropTypes.object,
    additionalInfoOnly: PropTypes.bool,
    overlayAdditionalInfo: PropTypes.object,
    childrenClassNames: PropTypes.shape({
      input: PropTypes.string,
      placeholderOverlay: PropTypes.string,
      overlayAdditionalInfo: PropTypes.string,
    }),
    hasFadeOnRight: PropTypes.bool, // забеление текста
    hasBottomLine: PropTypes.bool,
    withBorder: PropTypes.bool,
    isActive: PropTypes.bool.isRequired,
    parentInputRef: PropTypes.func,
    enableAutoComplete: PropTypes.bool,
    autoCompleteName: PropTypes.string,
    autoComplete: deprecatedPropType(
      PropTypes.string,
      'Use enableAutoComplete and autoCompleteName instead'
    ),
  };

  static defaultProps = {
    ...Control.defaultProps,
    childrenClassNames: {},
    hasFadeOnRight: false,
    hasBottomLine: false,
    withBorder: false,
    isActive: false,
    parentInputRef: _noop,
    enableAutoComplete: false,
    autoCompleteName: '',
  };

  getInputAutoCompleteAttributeValue(enableAutoComplete, autoCompleteName) {
    if (!enableAutoComplete) {
      return 'off';
    }

    if (autoCompleteName) {
      return autoCompleteName;
    }

    return 'on';
  }

  getInputProps() {
    const {
      additionalInfo,
      additionalInfoOnly,
      enableAutoComplete,
      autoCompleteName,
      ...inputProps
    } = super.getInputProps();

    const { focusedStatePlaceholder } = this.props;

    if (focusedStatePlaceholder && inputProps.focused) {
      inputProps.placeholder = focusedStatePlaceholder;
    }

    if (!inputProps.hasOwnProperty('autoComplete')) {
      inputProps.autoComplete = this.getInputAutoCompleteAttributeValue(
        enableAutoComplete,
        autoCompleteName
      );
    }

    return inputProps;
  }

  select() {
    const { inputRef } = this;

    if (!inputRef) {
      return;
    }

    setTimeout(() => inputRef.setSelectionRange(0, inputRef.value.length), 50);
  }

  handleMouseUp(e) {
    if (e) {
      e.preventDefault();
      e.nativeEvent.preventDefault();
    }
  }

  isFocused() {
    return this.state.focused;
  }

  setInputRef = el => {
    const { parentInputRef } = this.props;
    parentInputRef(el);

    this.inputRef = el;
  };

  render() {
    const {
      additionalInfo,
      additionalInfoOnly,
      overlayAdditionalInfo,
      childrenClassNames,
      hasFadeOnRight,
      hasBottomLine,
      withBorder,
      isActive,
      className,
    } = this.props;

    const {
      isTouched,
      isFocused,
      isHovered,
      isValid,
      isPristine,
      isDirty,
      hasError,
      hasValue,
      isError,
    } = this.getModificators();

    const inputProps = _omit(this.getInputProps(), ['valid', 'touched', 'parse']);

    let placeholder = null;

    if (
      !inputProps.value &&
      !isServerSideRender &&
      !isPlaceholderSupported &&
      inputProps.placeholder
    ) {
      placeholder = (
        <div className={cx('placeholderOverlay', childrenClassNames.placeholderOverlay)}>
          {inputProps.placeholder}
        </div>
      );
    }

    const inputStyleNames = cx('input', childrenClassNames.input, {
      'input--isDisabled': Boolean(inputProps.disabled),
      'input--isError': isError,
    });

    const isShowAdditionalInfo = additionalInfoOnly || !isFocused;

    return (
      <div
        {...this.getContainerProps()}
        className={cx(
          'RainbowInput',
          {
            'RainbowInput--isTouched': isTouched,
            'RainbowInput--isFocused': isFocused,
            'RainbowInput--isHovered': isHovered,
            'RainbowInput--isActive': isActive,
            'RainbowInput--isValid': isValid,
            'RainbowInput--isPristine': isPristine,
            'RainbowInput--isDirty': isDirty,
            'RainbowInput--hasError': hasError,
            'RainbowInput--hasValue': hasValue,
            'RainbowInput--hasFadeOnRight': hasFadeOnRight,
            'RainbowInput--hasOverlayAdditionalInfo': Boolean(overlayAdditionalInfo),
            'RainbowInput--hasBottomLine': hasBottomLine,
            'RainbowInput--withBorder': withBorder,
          },
          className
        )}
      >
        {placeholder}
        {isShowAdditionalInfo && (
          <div
            className={cx('input', 'additionalInfo', childrenClassNames.input, {
              'input--isDisabled': Boolean(inputProps.disabled),
              'input--isError': isError,
            })}
          >
            {additionalInfo}
          </div>
        )}
        <input
          type="text"
          {...inputProps}
          onMouseUp={this.handleMouseUp}
          value={inputProps.value || ''}
          className={inputStyleNames}
          data-error={isError && !isFocused ? 'true' : 'false'}
          ref={this.setInputRef}
        />
        <label
          className={cx(
            'overlayAdditionalInfo',
            isFocused && 'overlayAdditionalInfo--isFocused',
            childrenClassNames.overlayAdditionalInfo
          )}
        >
          {overlayAdditionalInfo}
        </label>
        {hasFadeOnRight && <div className={cx('fadeOnRight')} />}
      </div>
    );
  }
}

export default RainbowInput;
